import React from 'react';
import { ProductTileContainerSkeleton, ProductTileContentContainer, StyledProductTileContainer } from './ProductTile.styles';
import { useProductTile } from './useProductTile';
import { ProductTileProps } from './ProductTile.types';
import { BaseProductDetails, QuickAddForm } from './components';
import { useProductTileDesktopView } from './useProductTileDeskTopView';
import { SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { Box } from '@withjoy/joykit';
import { useCatalogRouting } from '../../useCatalogRouting';

interface Props
  extends Readonly<{
    isTypeCashOrCharity?: boolean;
    productId: string;
    shouldOpenPdp?: boolean;
  }> {}

const BaseProductDetailsWrapper: React.FC<Props> = ({ isTypeCashOrCharity, productId, children, shouldOpenPdp }) => {
  const { getCatalogItemPath } = useCatalogRouting();
  const catalogItemPath = getCatalogItemPath(productId);

  if (isTypeCashOrCharity) {
    return <>{children}</>;
  }

  if (!shouldOpenPdp) {
    return (
      <Box as="div" width="100%" height="100%">
        {children}
      </Box>
    );
  }
  return (
    <Box as="a" width="100%" height="100%" href={catalogItemPath}>
      {children}
    </Box>
  );
};

export const ProductTile: React.FC<ProductTileProps> = React.memo(props => {
  const { isMobile, index, shouldOpenPdp } = props;

  const {
    getQuickAddFormProps,
    getBaseProductDetailsProps,
    getRootProps,

    styledProductTileContainerRef,
    baseProductDetailsRef,
    quickAddFormRef,
    containerRef,
    isOpen,
    height
  } = useProductTileDesktopView({ isMobile: !!isMobile });

  const {
    handleOnCashFundProductClick,
    handleDetailClick,
    handleDetailKeyPress,
    isAddLoading,
    isFullCover,
    productDisplayFields,
    handleOnViewMoreDimensionClick,
    handleMobileQuickAddClick,
    changeActiveDimensionOption,
    showColorSwatch,
    swatchColorOptions,
    setProductSelected,
    selectedProduct,
    isTypeCashOrCharity,
    initiallySelectedProduct
  } = useProductTile(props);

  let offset: string = '0px';
  if (Array.isArray(swatchColorOptions) && swatchColorOptions.length > 0) {
    offset = '34px';
  }

  const { product, ...quickAddFormProps } = props;

  const isGroupItem = initiallySelectedProduct?.tilePresentation?.presentationType === 'parentDetails';

  return (
    <StyledProductTileContainer {...getRootProps()} $isactive={isOpen} $height={height} ref={styledProductTileContainerRef}>
      <ProductTileContentContainer $isactive={isOpen} ref={containerRef}>
        <BaseProductDetailsWrapper isTypeCashOrCharity={isTypeCashOrCharity} productId={product.id} shouldOpenPdp={shouldOpenPdp}>
          <BaseProductDetails
            onDetailClick={handleDetailClick}
            onDetailKeyPress={handleDetailKeyPress}
            isAddLoading={isAddLoading}
            isFullCover={isFullCover}
            initiallySelectedProduct={initiallySelectedProduct}
            productDisplayFields={productDisplayFields}
            onViewMoreDimensionClick={handleOnViewMoreDimensionClick}
            onChangeActiveDimensionOption={changeActiveDimensionOption}
            onQuickAddClick={handleMobileQuickAddClick}
            showColorSwatch={showColorSwatch}
            swatchColorOptions={swatchColorOptions}
            isMobile={props.isMobile}
            isHovered={isOpen}
            isGroupItem={isGroupItem}
            preload={props.index <= 8}
            ref={baseProductDetailsRef}
            storefrontInfo={product.storefrontInfo}
            hasAnyStorefrontWarning={props.hasAnyStorefrontWarning}
            variant={props.variant}
            productRating={product.rating?.stats}
            {...getBaseProductDetailsProps()}
          />
        </BaseProductDetailsWrapper>
        {!isMobile && (
          <QuickAddForm
            {...quickAddFormProps}
            productId={product.id}
            isOpen={isOpen}
            showQuantity={!isTypeCashOrCharity}
            onViewDetailsClick={handleDetailClick}
            offset={offset}
            selectedProduct={selectedProduct}
            setProductSelected={setProductSelected}
            onEditAndAddCashFund={handleOnCashFundProductClick}
            itemPosition={index}
            {...getQuickAddFormProps()}
            ref={quickAddFormRef}
          />
        )}
      </ProductTileContentContainer>
    </StyledProductTileContainer>
  );
});

export const ProductTileSkeleton = (props: { variant: 'gridItem' | 'carouselItem'; isMobile: boolean | undefined }) => {
  const isGridVariant = props.variant === 'gridItem';
  return (
    <ProductTileContainerSkeleton isMobile={props.isMobile}>
      <SkeletonThumbnail
        skeletonSize="0px"
        overrides={{
          PlaceholderRoot: {
            props: {
              width: '100%',
              paddingTop: '100%',
              backgroundColor: 'white'
            }
          }
        }}
      />
      <Box width="100%" marginTop={5}>
        <SkeletonText rows={1} width={100} skeletonHeight={3} margin={isGridVariant ? '0 auto' : ''} />
        <SkeletonText
          display={'flex'}
          flexDirection={'column'}
          alignItems={isGridVariant ? 'center' : 'flex-start'}
          margin={4}
          marginLeft={isGridVariant ? '' : '0'}
          enableVaryingRowWidth={true}
          rows={2}
          rowWidths={[80, 70]}
          skeletonHeight={14}
          spacing={'10px'}
        />
        <SkeletonText margin={isGridVariant ? '12px auto' : ''} width={48} rows={1} skeletonHeight={'10px'} />
      </Box>
    </ProductTileContainerSkeleton>
  );
};

ProductTile.displayName = 'ProductTileV2';
